import {
  FETCH_ADJUSTMENT_PERIODS,
  FETCH_ADJUSTMENT_PERIODS_SUCCESS,
  FETCH_ADJUSTMENT_PERIODS_FAILURE,
  SELECT_ADJUSTMENT_PERIOD,
  FETCH_COMPANY_CODES_FOR_BEGINNING_BALANCES,
  FETCH_COMPANY_CODES_FOR_BEGINNING_BALANCES_SUCCESS,
  FETCH_COMPANY_CODES_FOR_BEGINNING_BALANCES_FAILURE,
  FETCH_CONNECTIONS_FOR_BEGINNING_BALANCES,
  FETCH_CONNECTIONS_FOR_BEGINNING_BALANCES_SUCCESS,
  FETCH_CONNECTIONS_FOR_BEGINNING_BALANCES_FAILURE,
  FETCH_BEGINNING_BALANCE_FOR_COMPANY_CODE,
  FETCH_BEGINNING_BALANCE_FOR_COMPANY_CODE_SUCCESS,
  FETCH_BEGINNING_BALANCE_FOR_COMPANY_CODE_FAILURE,
  UPLOAD_BEGINING_BALANCES_FILE,
  UPLOAD_BEGINING_BALANCES_FILE_SUCCESS,
  UPLOAD_BEGINING_BALANCES_FILE_FAILURE,
  SELECT_COMPANY_CODE_FOR_BEGINNING_BALANCES,
  SELECT_CONNECTION_FOR_BEGINNING_BALANCES,
  FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES,
  FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES_SUCCESS,
  FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES_FAILURE,
  FETCH_BEGINNING_BALANCE_IMPORT_DATA_SUCCESS,
  FETCH_BEGINNING_BALANCE_IMPORT_DATA_FAILURE,
  SELECT_COMPANY_CODE_FOR_BEGINNING_BALANCES_CONNECTION,
} from './types';

import {
  fetchAdjustmentPeriods as fetchAdjustmentPeriodsApi,
  fetchCompanyCodes as fetchCompanyCodesApi,
  fetchConnections as fetchConnectionsApi,
  fetchBeginningBalancesForCompanyCode as fetchBeginningBalancesForCompanyCodeApi,
  fetchCompanyCodesForSelectedConnection as fetchCompanyCodesForSelectedConnectionApi,
  fetchBeginningBalancesImportStatus as fetchBeginningBalancesImportStatusApi,
  fetchValidationSummary as fetchValidationSummaryApi,
} from '../api';

import { fetchingError, uploadBalanceFileAction, uploadBatchBalancesFileAction } from '../../../shared/store/actions';
import { errorNotification, successNotification } from '../../../shared/store/notificationActions';

import ModalNames from '../modalNames';
import getModalActions from '../../../shared/modals/getModalActions';
import {
  adjustmentPeriodSelector,
  currentAdjustmentPeriodSelector,
  companyCodeSelector,
  connectionSelector,
} from './selectors';
import { userRoleSelector } from '../../../shared/store/selectors';

const fetchAdjustmentPeriods = () => async dispatch => {
  dispatch({
    type: FETCH_ADJUSTMENT_PERIODS,
  });

  try {
    const response = await fetchAdjustmentPeriodsApi();

    return dispatch({
      type: FETCH_ADJUSTMENT_PERIODS_SUCCESS,
      adjustmentPeriods: response,
    });
  } catch (error) {
    dispatch({
      type: FETCH_ADJUSTMENT_PERIODS_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchCompanyCodes = period => async dispatch => {
  dispatch({
    type: FETCH_COMPANY_CODES_FOR_BEGINNING_BALANCES,
  });

  try {
    const response = await fetchCompanyCodesApi(period);

    return dispatch({
      type: FETCH_COMPANY_CODES_FOR_BEGINNING_BALANCES_SUCCESS,
      companyCodes: response,
    });
  } catch (error) {
    dispatch({
      type: FETCH_COMPANY_CODES_FOR_BEGINNING_BALANCES_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchConnections = period => async dispatch => {
  dispatch({
    type: FETCH_CONNECTIONS_FOR_BEGINNING_BALANCES,
  });

  try {
    const response = await fetchConnectionsApi(period);

    return dispatch({
      type: FETCH_CONNECTIONS_FOR_BEGINNING_BALANCES_SUCCESS,
      connections: response,
    });
  } catch (error) {
    dispatch({
      type: FETCH_CONNECTIONS_FOR_BEGINNING_BALANCES_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchBeginningBalancesForCompanyCode = (period, companyCode) => async dispatch => {
  dispatch({
    type: FETCH_BEGINNING_BALANCE_FOR_COMPANY_CODE,
  });

  try {
    const response = await fetchBeginningBalancesForCompanyCodeApi(period, companyCode);
    const { validationSummary, ignoredCompanyCodes } = await fetchValidationSummaryApi(period, companyCode);

    return dispatch({
      type: FETCH_BEGINNING_BALANCE_FOR_COMPANY_CODE_SUCCESS,
      beginningBalances: response,
      validationSummary,
      ignoredCompanyCodes,
    });
  } catch (error) {
    dispatch({
      type: FETCH_BEGINNING_BALANCE_FOR_COMPANY_CODE_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchCompanyCodesForSelectedConnection = connection => async dispatch => {
  dispatch({
    type: FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES,
  });

  try {
    const companyCodesForConnection = await fetchCompanyCodesForSelectedConnectionApi(connection);

    return dispatch({
      type: FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES_SUCCESS,
      companyCodesForConnection,
    });
  } catch (error) {
    dispatch({
      type: FETCH_COMPANY_CODES_FOR_CONNECTION_BEGINNING_BALANCES_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const fetchInitialDataForBeginningBalances = period => async (dispatch, getState) => {
  await dispatch(fetchAdjustmentPeriods(period));

  const adjustmentPeriod = adjustmentPeriodSelector(getState());

  await Promise.all([
    dispatch(fetchCompanyCodes(adjustmentPeriod)),
    dispatch(fetchConnections(adjustmentPeriod)),
  ]);
};

const fetchBeginningBalancesImportStatus = (adjustmentPeriod, connectionName) => async dispatch => {
  try {
    const {
      importData,
      ignoredCompanyCodes,
    } = await fetchBeginningBalancesImportStatusApi(adjustmentPeriod, connectionName);

    return dispatch({
      type: FETCH_BEGINNING_BALANCE_IMPORT_DATA_SUCCESS,
      importData,
      ignoredCompanyCodes,
    });
  } catch (error) {
    dispatch({
      type: FETCH_BEGINNING_BALANCE_IMPORT_DATA_FAILURE,
    });

    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const selectCompanyCodeForBeginningBalances = companyCode => (dispatch, getState) => {
  const adjustmentPeriod = adjustmentPeriodSelector(getState());

  dispatch(fetchBeginningBalancesForCompanyCode(adjustmentPeriod, companyCode));
  dispatch({
    type: SELECT_COMPANY_CODE_FOR_BEGINNING_BALANCES,
    companyCode,
  });
};

export const selectConnectionForBeginningBalances = (adjustmentPeriod, connection) => dispatch => {
  dispatch(fetchCompanyCodesForSelectedConnection(connection.label));
  dispatch(fetchBeginningBalancesImportStatus(adjustmentPeriod, connection.label));
  dispatch({
    type: SELECT_CONNECTION_FOR_BEGINNING_BALANCES,
    connection,
  });
};

export const selectCompanyCodeForBeginningBalancesConnection = companyCode => (dispatch, getState) => {
  const adjustmentPeriod = adjustmentPeriodSelector(getState());

  dispatch(fetchBeginningBalancesForCompanyCode(adjustmentPeriod, companyCode));
  dispatch({
    type: SELECT_COMPANY_CODE_FOR_BEGINNING_BALANCES_CONNECTION,
    companyCode,
  });
};

export const selectPeriodForBeginningBalances = period => async (dispatch, getState) => {
  const selectedCompanyCode = getState().administration.beginningBalances.companyCode;
  dispatch({
    type: SELECT_ADJUSTMENT_PERIOD,
    period,
  });
  await dispatch(fetchCompanyCodes(period));
  if (selectedCompanyCode
    && getState().administration.beginningBalances.companyCodes.find(companyCode =>
      companyCode.companyCode === selectedCompanyCode)
  ) {
    dispatch(selectCompanyCodeForBeginningBalances(selectedCompanyCode));
  }
};

const {
  showAction: showImportBeginningBalancesModal,
  hideAction: hideImportBeginningBalancesModal,
} = getModalActions(ModalNames.ImportBeginningBalances);

export {
  showImportBeginningBalancesModal,
  hideImportBeginningBalancesModal,
};

const {
  showAction: showImportBatchBeginningBalancesModal,
  hideAction: hideImportBatchBeginningBalancesModal,
} = getModalActions(ModalNames.ImportBatchBeginningBalances);

export {
  showImportBatchBeginningBalancesModal,
  hideImportBatchBeginningBalancesModal,
};

export const importBeginningBalancesFile = ({ file }) => async (dispatch, getState) => {
  const state = getState();

  const userRole = userRoleSelector(state);
  const companyCode = companyCodeSelector(state);
  const adjustmentPeriod = adjustmentPeriodSelector(state);
  const currentAdjustmentPeriod = currentAdjustmentPeriodSelector(state);

  const action = () => ({
    type: UPLOAD_BEGINING_BALANCES_FILE,
  });

  const successAction = () => async dispatch => {
    await dispatch(fetchBeginningBalancesForCompanyCode(adjustmentPeriod, companyCode));

    dispatch({
      type: UPLOAD_BEGINING_BALANCES_FILE_SUCCESS,
    });
  };

  const failureAction = () => ({
    type: UPLOAD_BEGINING_BALANCES_FILE_FAILURE,
  });

  return dispatch(uploadBalanceFileAction({
    action,
    successAction,
    failureAction,

    validationData: {
      companyCodes: [{ value: companyCode }],
      userRole,
      period: { name: adjustmentPeriod },
      currentPeriod: { name: currentAdjustmentPeriod.periodName },
      isBeginningBalance: true,
    },

    isBeginningBalance: true,
    companyCode,
    messageLabel: 'Beginning Balance',
    file,
  }));
};

export const importBatchBeginningBalancesFile = ({
  file,
  connectionName,
}) => async (dispatch, getState) => {
  const state = getState();

  const userRole = userRoleSelector(state);
  const currentAdjustmentPeriod = currentAdjustmentPeriodSelector(state);
  const adjustmentPeriodName = adjustmentPeriodSelector(state);
  const connection = connectionSelector(state);

  const batchProvisionStarted = () => ({
    type: UPLOAD_BEGINING_BALANCES_FILE,
  });

  const batchProvisionFailure = () => ({
    type: UPLOAD_BEGINING_BALANCES_FILE_FAILURE,
  });

  const batchProvisionSuccess = () => {
    dispatch(successNotification('Beginning Balance File Import Successful'));
    dispatch(selectConnectionForBeginningBalances(adjustmentPeriodName, connection));
    return {
      type: UPLOAD_BEGINING_BALANCES_FILE_SUCCESS,
    };
  };

  dispatch(uploadBatchBalancesFileAction({
    file,
    connectionName,
    adjustmentPeriodName,
    isBeginningBalance: true,
    calculateProvisions: true,
    loadTrialBalances: true,
    skipFileUpload: false,
    messageLabel: 'Batch Beginning Balance',

    validationData: {
      userRole,
      period: { name: adjustmentPeriodName },
      currentPeriod: { name: currentAdjustmentPeriod.periodName },
      isBeginningBalance: true,
    },

    action: batchProvisionStarted,
    successAction: batchProvisionSuccess,
    failureAction: batchProvisionFailure,
  }));
};
